<script setup lang="ts">
import { login } from '@/api/auth'
import { isSignupEnabled } from '@/composables/useSignupEnabled'
import { ApiError } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import type { Provider, ProviderUser } from '@/types'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: {
    layout: 'blank',
    public: true,
    publicOnly: true,
  },
})

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()

const form = ref({
  email: '',
  password: '',
})

const isPasswordVisible = ref(false)
const loading = ref(false)
const error = ref('')

/*
 * Las cuentas de proveedor se dan de alta por invitación: el CTA manda a
 * escribirnos, no a un formulario. El flag permite reabrir el alta pública sin
 * deploy, así que se pregunta en vez de asumir.
 *
 * Arranca en `false` para no llegar a mostrar "Create an account" un instante y
 * que se convierta en "Request an account" al llegar la respuesta.
 */
const signupOpen = ref(false)

onMounted(async () => {
  signupOpen.value = await isSignupEnabled()
})

const submit = async () => {
  error.value = ''

  if (!form.value.email || !form.value.password) {
    error.value = 'Enter your email and password.'

    return
  }

  loading.value = true
  try {
    const session = await login(form.value.email.trim(), form.value.password)

    /*
     * `setSession` por sí solo ya pone `isAuthenticated` en true. A propósito
     * NO se hace `await auth.refreshMe()` acá: el dashboard re-consulta
     * /account/me en su onMounted, y esperar dos veces abre una ventana donde
     * el guard puede leer estado stale (CLAUDE.md §3.6).
     */
    /*
     * `AuthSession` trae un subconjunto de `Provider`/`ProviderUser` (el login
     * no devuelve, por ejemplo, `verificationStatus`). Se castea a propósito:
     * el dato autoritativo llega enseguida por `/account/me`.
     */
    auth.setSession(
      session.provider as unknown as Provider,
      session.providerUser as unknown as ProviderUser,
      session.token.accessToken,
      session.token.refreshToken,
    )

    const nextRaw = route.query.next
    const next = typeof nextRaw === 'string' && nextRaw ? nextRaw : '/dashboard'

    // `replace` evita volver al login con el botón atrás; el `await` surfacea
    // un NavigationFailure en vez de tragárselo.
    await router.replace(next)
  }
  catch (e) {
    /*
     * El genérico tapaba cualquier excepción posterior a la respuesta del
     * login —la sesión se guarda y después navega—, así que un fallo al
     * navegar se leía como "credenciales mal". Se deja el mensaje corto en
     * pantalla y el error completo en consola, que es donde se diagnostica.
     */
    if (!(e instanceof ApiError))
      console.error('[login] failed after the request succeeded:', e)

    error.value = e instanceof ApiError ? e.message : 'Login failed. Check the browser console.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    no-gutters
    class="auth-wrapper"
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Welcome back 👋🏻
          </h4>
          <p class="mb-0">
            Log in to your Blue Trails provider account.
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="form.email"
                  autofocus
                  label="Email"
                  type="email"
                  autocomplete="username"
                  placeholder="you@divecenter.com"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="form.password"
                  label="Password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="current-password"
                  :disabled="loading"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap justify-end my-5 gap-2">
                  <RouterLink
                    class="text-primary"
                    :to="{ name: 'forgot-password' }"
                  >
                    Forgot Password?
                  </RouterLink>
                </div>

                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  Login
                </VBtn>
              </VCol>

              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>New on Blue Trails?</span>
                <RouterLink
                  v-if="signupOpen"
                  class="text-primary d-inline-block ms-1"
                  :to="{ name: 'register' }"
                >
                  Create an account
                </RouterLink>
                <!--
                  Sin alta pública el destino sale del portal: va al formulario
                  de bluetrails.eu/for-dive-centers, que abre un pedido de
                  acceso y nos llega por mail con el Reply-To del solicitante.

                  Antes era un `mailto:`, que en una máquina sin cliente de
                  correo configurado no hace absolutamente nada — el link
                  parecía roto.

                  Se abre en otra pestaña para no tirar abajo el login de quien
                  en realidad ya tiene cuenta y se equivocó de link.
                -->
                <a
                  v-else
                  class="text-primary d-inline-block ms-1"
                  href="https://bluetrails.eu/for-dive-centers/#request-access"
                  target="_blank"
                  rel="noopener"
                >
                  Request an account
                </a>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
